import React, { useEffect, useRef, useState } from "react";
import { Icon } from "@anchor/react-components/dist/lib/components";
import { McTooltip, McIcon, McModal, McButton } from "@maersk-global/mds-react-wrapper/components-core";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import UserInfo from "../../../context/UserAccessContext";
import { IsLocked } from "../../IsLocked/IsLocked";

const hasRealText = (html) => {
  if (!html) return false;
  return html.replace(/<[^>]*>/g, "").trim().length > 0;
};

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const QUILL_FORMATS = [
  "header",
  "bold", "italic", "underline",
  "list", "bullet",
  "link",
];

function BusinessImpactSection(props) {
  const [value, setValue] = useState(props.impact || "");
  const [modalOpen, setModalOpen] = useState(false);
  const savingRef = useRef(false);
  const { graphData } = UserInfo();

  useEffect(() => {
    setValue(props.impact || "");
  }, [props.impact]);

  const handleOpen = () => setModalOpen(true);
  const handleClose = () => setModalOpen(false);

  const onSave = () => {
    savingRef.current = true;
    props.dispatch({
      type: "HANDLE EDITOR CHANGE",
      field: "incidentSummaryState",
      payload: { ...props.incidentSummaryState, impact: value },
    });
    props.dispatch({
      type: "HANDLE EDITOR CHANGE",
      field: "changedFileds",
      payload: (prev) => ({ ...prev, impact: true }),
    });
    handleClose();
    if (props.onAutoSave) {
      setTimeout(() => { props.onAutoSave(); savingRef.current = false; }, 100);
    } else {
      savingRef.current = false;
    }
  };

  const isLocked = Array.isArray(props.lockedFields) &&
    props.lockedFields.find(f => f.fieldName === "impact" && f.lockedBy !== graphData?.username);

  return (
    <>
      <div id="section-business-impact" className="sre-section-card">
        <div className="sre-section-header">
          <span className="sre-section-header-title">Business Impact</span>
          {hasRealText(value) ? (
            <span className="sre-section-complete-badge">&#10003; Complete</span>
          ) : (
            <span className="sre-section-warning-badge">&#9888; Incomplete</span>
          )}
          <McTooltip position="bottom-right" fit="small" width={320}>
            <McIcon slot="trigger" icon="info-circle" size="16" style={{ cursor: "pointer", color: "#fff" }} />
            <div style={{ fontSize: "12px", lineHeight: "1.6", padding: "4px 0" }}>
              <strong>SRE Best Practices — Business Impact</strong>
              <ul style={{ margin: "6px 0 0", paddingLeft: "16px" }}>
                <li>Describe the customer-facing and business impact of the incident</li>
                <li>Quantify affected users, transactions, or revenue where possible</li>
                <li>Include duration and geographic scope of the impact</li>
                <li>Note any SLA breaches or contractual obligations affected</li>
              </ul>
            </div>
          </McTooltip>
          <span className="sre-section-header-badge" style={{ flex: 1 }}>
            Manual entry required
          </span>
          {!props.postmortemIsIncompleteState && (
            <button className="sre-section-header-action" onClick={handleOpen}>
              <Icon name="pencil" style={{ fontSize: "12px" }} />
              Edit
            </button>
          )}
        </div>

        <div
          className={`sre-section-body${!props.postmortemIsIncompleteState ? " sre-section-body--editable" : ""}`}
          onClick={!props.postmortemIsIncompleteState ? handleOpen : undefined}
        >
          <IsLocked field="impact" lockedFields={props.lockedFields} graphData={graphData} />
          <div style={{
            fontSize: "14px", lineHeight: 1.7,
            color: "var(--mds_brand_appearance_default_default_text-color, #141414)",
            wordBreak: "break-word",
            ...(isLocked ? { border: "1px solid red", padding: "10px", borderRadius: "5px" } : {}),
          }}>
            {hasRealText(value) ? (
              <div dangerouslySetInnerHTML={{ __html: value.replace(/\n/g, "<br/>") }} />
            ) : (
              <span style={{ color: "var(--mds_brand_appearance_default_default_text-color-secondary, #999)", fontStyle: "italic" }}>
                Describe the business and customer impact of this incident
              </span>
            )}
          </div>
        </div>
      </div>

      <McModal
        heading="Business Impact"
        open={modalOpen}
        dimension="medium"
        backdropcloseactiondisabled={true}
        escapecloseactiondisabled={true}
        closed={handleClose}
      >
        <div style={{ minHeight: "300px" }}>
          <ReactQuill
            theme="snow"
            modules={QUILL_MODULES}
            formats={QUILL_FORMATS}
            value={value}
            onChange={setValue}
            style={{ background: "#fff", minHeight: "240px" }}
          />
        </div>
        <McButton slot="primaryAction" appearance="primary" click={onSave}>
          Save
        </McButton>
        <McButton slot="secondaryAction" appearance="neutral" variant="outlined" click={handleClose}>
          Cancel
        </McButton>
      </McModal>
    </>
  );
}

export default BusinessImpactSection;
